/* eslint-disable */
// ─────────────────────────────────────────────────────
// Voice — browser speechSynthesis ONLY
// ElevenLabs removed entirely until curriculum approved
// and paid plan activated. No async, no cache, no routing.
// ─────────────────────────────────────────────────────

let currentUtterance = null;
let selectedVoice = null;
let lastCancelAt = 0;

// "Busy" = one of our utterances is queued or speaking. Set the moment speak is
// called (not when Chrome's async onstart arrives) and cleared only when that
// utterance finishes or is stopped. The lesson's Continue gate subscribes to this
// so it can never be enabled while Ms. Momo's speech is pending or playing (Rule 1).
const busyListeners = new Set();
let lastBusy = false;
function notifyBusy() {
  const busy = currentUtterance !== null;
  if (busy === lastBusy) return;
  lastBusy = busy;
  busyListeners.forEach(cb => { try { cb(busy); } catch (e) {} });
}
// Synchronous check (no React render lag): is a line queued or playing right now?
export function isSpeechBusy() {
  return currentUtterance !== null;
}
export function onSpeechBusyChange(cb) {
  busyListeners.add(cb);
  cb(lastBusy);
  return () => busyListeners.delete(cb);
}

// Pick the best available English voice once on load
function pickVoice() {
  const voices = window.speechSynthesis?.getVoices() || [];
  selectedVoice = voices.find(v =>
    v.lang.startsWith('en') && /samantha|victoria|karen|zira|google us english/i.test(v.name)
  ) || voices.find(v => v.lang.startsWith('en-'))
    || voices.find(v => v.lang.startsWith('en'))
    || voices[0]
    || null;
}

if (typeof window !== 'undefined' && window.speechSynthesis) {
  pickVoice();
  window.speechSynthesis.onvoiceschanged = pickVoice;
}

export function stopSpeech() {
  try { window.speechSynthesis?.cancel(); } catch(e) {}
  lastCancelAt = Date.now();
  currentUtterance = null;
  notifyBusy();
}

export function pendingAudioClear() {
  // No pending queue in direct mode — just cancel current speech
  stopSpeech();
}

export function unlockAudio() {
  // No-op in direct mode — gesture handled by caller
}

// Main speak function — called directly within click handlers
// No async, no ElevenLabs, no stopSpeech() before speak()
//
// Chrome's speechSynthesis has several silent-failure modes that left Ms. Momo
// "asleep" (no voice, buttons locked) until the student clicked something.
// Each utterance is therefore supervised by a small watchdog:
//   1. An utterance queued in the same tick as cancel() is sometimes dropped
//      without any event. We only cancel when something is actually playing,
//      speak after a short gap when we did cancel, and RETRY once if speech
//      hasn't started. onBlocked only fires if the retry also fails.
//   2. Chrome can leave the engine stuck in a paused state — resume() it.
//   3. Chrome's network ("Google …") voices stop after ~15s and never fire
//      onend. A pause()/resume() keep-alive every 10s prevents the cut-off.
//   4. onend is sometimes never delivered. If the engine has gone idle, or the
//      utterance runs far past any plausible length, we finish it ourselves.
// The callbacks keep their old contract: onEnd fires once when speech is over
// (never after a cancel by another speak/stopSpeech), onBlocked when audio
// could not start at all.
export function speakElevenLabs(text, {
  onStart, onEnd, onError, onBlocked, onBoundary
} = {}) {
  if (!text?.trim()) { onEnd?.(); return; }

  const synth = window.speechSynthesis;
  if (!synth) { onEnd?.(); return; }

  let started = false;
  let finished = false;
  let retried = false;
  let startedAt = 0;
  let idleTicks = 0;
  let lastKeepAlive = 0;
  let watchdog = null;
  const queuedAt = Date.now();
  // Generous ceiling: rate 0.92 speaks ~14 chars/s (~70ms/char).
  const maxSpeakMs = Math.max(8000, text.length * 130) + 4000;
  const networkVoice = !selectedVoice || selectedVoice.localService === false;

  function finish(cb) {
    if (finished) return;
    finished = true;
    clearInterval(watchdog);
    if (currentUtterance === u) currentUtterance = null;
    notifyBusy();
    cb?.();
  }

  function makeUtterance() {
    const utt = new SpeechSynthesisUtterance(text);
    utt.rate  = 0.92;
    utt.pitch = 1.05;
    utt.volume = 1;
    if (selectedVoice) utt.voice = selectedVoice;
    utt.onstart = () => {
      if (finished || currentUtterance !== utt) return;
      if (!started) { started = true; startedAt = Date.now(); onStart?.(); }
    };
    utt.onend = () => {
      if (currentUtterance !== utt) return;
      finish(onEnd);
    };
    utt.onerror = (e) => {
      if (currentUtterance !== utt) return;
      // Cancelled by another speak()/stopSpeech(): stay silent, as before.
      if (e?.error === 'interrupted' || e?.error === 'canceled') { finish(null); return; }
      finish(onEnd);
    };
    if (onBoundary) {
      utt.onboundary = (e) => {
        if (e.name === 'word') {
          idleTicks = 0;
          const word = text.slice(e.charIndex, e.charIndex + (e.charLength || 1)).trim();
          onBoundary(word, e.charIndex);
        }
      };
    }
    return utt;
  }

  let u = makeUtterance();
  currentUtterance = u;
  notifyBusy();

  function queue(utt) {
    const busy = synth.speaking || synth.pending;
    const justCancelled = Date.now() - lastCancelAt < 60;
    if (busy || justCancelled) {
      if (busy) { synth.cancel(); lastCancelAt = Date.now(); }
      // Speaking in the same tick as cancel() is what Chrome drops — leave a gap.
      setTimeout(() => { if (currentUtterance === utt && !finished) synth.speak(utt); }, 60);
    } else {
      synth.speak(utt);
    }
  }
  queue(u);

  watchdog = setInterval(() => {
    // Superseded by a newer utterance or stopped by stopSpeech(): stop watching.
    if (currentUtterance !== u) { finish(null); return; }
    if (synth.paused) synth.resume();
    const now = Date.now();

    if (!started) {
      // Some engines speak without firing onstart — treat speaking as started.
      if (synth.speaking && !synth.pending && now - queuedAt > 700) {
        started = true; startedAt = now; onStart?.();
        return;
      }
      // Nothing playing and nothing queued: the utterance was dropped — retry once.
      // (A slow-loading voice shows as pending, so it is left alone.)
      if (now - queuedAt > 900 && !retried && !synth.speaking && !synth.pending) {
        retried = true;
        synth.cancel();
        u = makeUtterance();
        currentUtterance = u;
        setTimeout(() => { if (currentUtterance === u && !finished) synth.speak(u); }, 60);
        return;
      }
      if ((now - queuedAt > 2200 && !synth.speaking && !synth.pending) || now - queuedAt > 8000) {
        finish(onBlocked || onEnd);
      }
      return;
    }

    // Keep-alive for network voices (Chrome's ~15s cut-off).
    if (networkVoice && synth.speaking && now - lastKeepAlive > 10000) {
      lastKeepAlive = now;
      synth.pause();
      synth.resume();
    }

    // Lost onend: engine idle for ~0.75s after speech started.
    if (!synth.speaking && !synth.pending) {
      idleTicks++;
      if (idleTicks >= 3) finish(onEnd);
    } else {
      idleTicks = 0;
    }

    // Hard ceiling — never leave the lesson locked behind a stuck utterance.
    if (now - startedAt > maxSpeakMs) {
      synth.cancel();
      finish(onEnd);
    }
  }, 250);
}

export function parseHighlights(text) {
  const mentions = [];
  let m;
  const sqRe = /\b([a-h])([1-8])\b/gi;
  while ((m = sqRe.exec(text)) !== null)
    mentions.push({ type: 'square', value: m[1].toLowerCase() + m[2], start: m.index });
  const fileRe = /\bfile\s+([a-h])\b|\b([a-h])\s+file\b/gi;
  while ((m = fileRe.exec(text)) !== null)
    mentions.push({ type: 'file', value: (m[1] || m[2]).toLowerCase(), start: m.index });
  const rankRe = /\brank\s+([1-8])\b|\b([1-8])(?:st|nd|rd|th)?\s+rank\b/gi;
  while ((m = rankRe.exec(text)) !== null)
    mentions.push({ type: 'rank', value: m[1] || m[2], start: m.index });
  ['king','queen','rook','bishop','knight','pawn'].forEach(p => {
    const pRe = new RegExp(`\\b${p}s?\\b`, 'gi');
    while ((m = pRe.exec(text)) !== null)
      mentions.push({ type: 'piece', value: p, start: m.index });
  });
  return mentions.sort((a, b) => a.start - b.start);
}
